import { useLanguage } from "@/contexts/LanguageContext";
import { LocalizedLink } from "@/components/LocalizedLink";
import { MapPin, Phone, MessageCircle, Mail, Clock, Facebook, Instagram, Youtube, Linkedin, Twitter, Send, Heart, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CLINIC_SAME_AS } from "@/lib/clinicSchema";
import { openCookiePreferences } from "@/lib/cookiePreferences";

// Map social hostnames -> icon + label so the footer stays in sync
// with the schema.org `sameAs` source of truth (CLINIC_SAME_AS).
const SOCIAL_ICON_MAP: Record<string, { icon: typeof Facebook; label: string; color: string }> = {
  "facebook.com": { icon: Facebook, label: "Facebook", color: "hover:text-blue-500" },
  "instagram.com": { icon: Instagram, label: "Instagram", color: "hover:text-pink-500" },
  "youtube.com": { icon: Youtube, label: "YouTube", color: "hover:text-red-500" },
  "linkedin.com": { icon: Linkedin, label: "LinkedIn", color: "hover:text-blue-600" },
  "twitter.com": { icon: Twitter, label: "Twitter", color: "hover:text-sky-500" },
  "x.com": { icon: Twitter, label: "X", color: "hover:text-sky-500" },
};

// ConsentManager (CMP) tercih ekranını açan footer linki
const PRIVACY_SETTINGS_LABEL = {
  tr: "Gizlilik ayarları",
  en: "Privacy settings",
  nl: "Privacy-instellingen",
  fr: "Paramètres de confidentialité",
  ar: "إعدادات الخصوصية",
};

export const Footer = () => {
  const { language } = useLanguage();

  const texts = {
    tr: {
      about: "Hakkımızda",
      aboutText: "Hair Art Clinics, Rijswijk'te (Den Haag bölgesi) saç ekimi danışmanlığı sunar ve sizi doğal görünümlü sonuçlar için deneyimli bir partner klinikle buluşturur.",
      quickLinks: "Hızlı Linkler",
      haartransplantatieDenHaag: "Haartransplantatie Den Haag",
      treatments: "Tedaviler",
      gallery: "Galeri",
      blog: "Blog",
      contact: "İletişim",
      bookConsultation: "Randevu Al",
      hairAnalysis: "AI Saç Analizi",
      locations: "Lokasyonlarımız",
      workingHours: "Çalışma Saatleri",
      weekdays: "Her gün: 10:00 - 18:00",
      legal: "Yasal",
      privacy: "Gizlilik Politikası",
      terms: "Kullanım Şartları",
      kvkk: "KVKK/GDPR",
      cookies: "Çerez Politikası",
      followUs: "Bizi Takip Edin",
      rights: "Tüm hakları saklıdır.",
      newsletter: "Bülten",
      newsletterText: "En son haberler ve özel teklifler için abone olun.",
      subscribe: "Abone Ol",
      emailPlaceholder: "E-posta adresiniz",
      madeWith: "ile yapıldı",
      getDirections: "Yol Tarifi",
      intermediary: "Hair Art Clinics cerrahi işlem yapmaz. Saç kaybı yaşayan ve saç ekimi yaptırmak isteyen danışanlar ile saç ekim kliniği arasında danışmanlık ve aracılık hizmeti sunar. Tüm işlemler, bağımsız partner klinikte yetkili sağlık profesyonelleri tarafından gerçekleştirilir. Sonuçtan memnun kalmamanız veya kliniğin verdiği sözleri tutmaması durumunda sizin adınıza devreye girer, süreç boyunca yanınızda oluruz.",
    },
    en: {
      about: "About Us",
      aboutText: "Hair Art Clinics provides hair restoration consultancy, connecting you with an experienced team and state-of-the-art equipment for natural-looking results at a partner clinic.",
      quickLinks: "Quick Links",
      haartransplantatieDenHaag: "Haartransplantatie Den Haag",
      treatments: "Treatments",
      gallery: "Gallery",
      blog: "Blog",
      contact: "Contact",
      bookConsultation: "Book Consultation",
      hairAnalysis: "AI Hair Analysis",
      locations: "Our Locations",
      workingHours: "Working Hours",
      weekdays: "Every day: 10:00 - 18:00",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      kvkk: "KVKK/GDPR",
      cookies: "Cookie Policy",
      followUs: "Follow Us",
      rights: "All rights reserved.",
      newsletter: "Newsletter",
      newsletterText: "Subscribe for latest news and exclusive offers.",
      subscribe: "Subscribe",
      emailPlaceholder: "Your email address",
      madeWith: "Made with",
      getDirections: "Get Directions",
      intermediary: "Hair Art Clinics does not perform surgical procedures. We provide consultancy and act as an intermediary between people experiencing hair loss who are considering a hair transplant and a hair transplant clinic. All procedures are carried out by licensed medical professionals at an independent partner clinic. If you are not satisfied with the outcome or the clinic does not keep its commitments, we step in on your behalf and stay by your side throughout the process.",
    },
    fr: {
      about: "À Propos",
      aboutText: "Hair Art Clinics propose un service de conseil en greffe de cheveux à Rijswijk (région de La Haye) et vous met en relation avec une clinique partenaire expérimentée pour des résultats naturels.",
      quickLinks: "Liens Rapides",
      haartransplantatieDenHaag: "Haartransplantatie Den Haag",
      treatments: "Traitements",
      gallery: "Galerie",
      blog: "Blog",
      contact: "Contact",
      bookConsultation: "Réserver",
      hairAnalysis: "Analyse IA",
      locations: "Nos Emplacements",
      workingHours: "Heures d'ouverture",
      weekdays: "Tous les jours : 10:00 - 18:00",
      legal: "Légal",
      privacy: "Politique de Confidentialité",
      terms: "Conditions d'utilisation",
      kvkk: "RGPD",
      cookies: "Politique de Cookies",
      followUs: "Suivez-nous",
      rights: "Tous droits réservés.",
      newsletter: "Newsletter",
      newsletterText: "Abonnez-vous pour les dernières nouvelles.",
      subscribe: "S'abonner",
      emailPlaceholder: "Votre email",
      madeWith: "Fait avec",
      getDirections: "Itinéraire",
      intermediary: "Hair Art Clinics ne réalise aucune intervention chirurgicale. Nous offrons un service de conseil et d'intermédiaire entre les personnes confrontées à une perte de cheveux qui envisagent une greffe et une clinique de greffe capillaire. Toutes les interventions sont réalisées par des professionnels de santé agréés dans une clinique partenaire indépendante. Si vous n'êtes pas satisfait du résultat ou si la clinique ne respecte pas ses engagements, nous intervenons en votre nom et restons à vos côtés tout au long du parcours.",
    },
    nl: {
      about: "Over Ons",
      aboutText: "Hair Art Clinics biedt haarherstel-advies, en verbindt u met een ervaren team en moderne apparatuur voor natuurlijke resultaten bij een partnerkliniek.",
      quickLinks: "Snelle Links",
      haartransplantatieDenHaag: "Haartransplantatie Den Haag",
      treatments: "Behandelingen",
      gallery: "Galerij",
      blog: "Blog",
      contact: "Contact",
      bookConsultation: "Boek Consult",
      hairAnalysis: "AI Haaranalyse",
      locations: "Onze Locaties",
      workingHours: "Openingstijden",
      weekdays: "Elke dag: 10:00 - 18:00",
      legal: "Juridisch",
      privacy: "Privacybeleid",
      terms: "Gebruiksvoorwaarden",
      kvkk: "AVG/GDPR",
      cookies: "Cookiebeleid",
      followUs: "Volg Ons",
      rights: "Alle rechten voorbehouden.",
      newsletter: "Nieuwsbrief",
      newsletterText: "Abonneer voor het laatste nieuws.",
      subscribe: "Abonneren",
      emailPlaceholder: "Uw e-mailadres",
      madeWith: "Gemaakt met",
      getDirections: "Routebeschrijving",
      intermediary: "Hair Art Clinics voert zelf geen chirurgische ingrepen uit. Wij bieden advies en bemiddeling tussen mensen met haarverlies die een haartransplantatie overwegen en een haartransplantatiekliniek. Alle behandelingen worden uitgevoerd door bevoegde zorgprofessionals in een onafhankelijke partnerkliniek. Bent u niet tevreden over het resultaat of komt de kliniek haar afspraken niet na, dan komen wij namens u in actie en staan wij het hele traject aan uw zijde.",
    },
    ar: {
      about: "من نحن",
      aboutText: "تقدم Hair Art Clinics خدمات استشارية لزراعة الشعر في رايسفايك (منطقة لاهاي) وتربطك بعيادة شريكة ذات خبرة للحصول على نتائج طبيعية.",
      quickLinks: "روابط سريعة",
      haartransplantatieDenHaag: "زراعة الشعر لاهاي",
      treatments: "العلاجات",
      gallery: "المعرض",
      blog: "المدونة",
      contact: "اتصل بنا",
      bookConsultation: "احجز استشارة",
      hairAnalysis: "تحليل الشعر بالذكاء الاصطناعي",
      locations: "مواقعنا",
      workingHours: "ساعات العمل",
      weekdays: "يومياً: 10:00 - 18:00",
      legal: "قانوني",
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة",
      kvkk: "GDPR",
      cookies: "سياسة ملفات تعريف الارتباط",
      followUs: "تابعنا",
      rights: "جميع الحقوق محفوظة.",
      newsletter: "النشرة الإخبارية",
      newsletterText: "اشترك للحصول على آخر الأخبار.",
      subscribe: "اشترك",
      emailPlaceholder: "بريدك الإلكتروني",
      madeWith: "صنع بـ",
      getDirections: "احصل على الاتجاهات",
      intermediary: "لا تُجري Hair Art Clinics أي عمليات جراحية. نحن نقدّم خدمات الاستشارة والوساطة بين الأشخاص الذين يعانون من تساقط الشعر ويرغبون في زراعته وبين عيادة زراعة الشعر. تُجرى جميع الإجراءات على يد مختصين صحيين مرخّصين في عيادة شريكة مستقلة. وإذا لم تكن راضيًا عن النتيجة أو لم تلتزم العيادة بوعودها، نتدخّل نيابةً عنك ونبقى إلى جانبك طوال الرحلة.",
    },
  };

  const t = texts[language as keyof typeof texts] || texts.en;

  const socialLinks = CLINIC_SAME_AS.map((href) => {
    const host = new URL(href).hostname.replace(/^www\./, "");
    const meta = SOCIAL_ICON_MAP[host];
    return meta ? { ...meta, href } : null;
  }).filter((x): x is { icon: typeof Facebook; label: string; color: string; href: string } => x !== null);

  return (
    <footer className="relative bg-gradient-to-b from-foreground to-foreground/95 text-background overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Top Section - Logo & Newsletter */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 pb-12 border-b border-background/10">
          <div>
            <LocalizedLink to="/" className="text-3xl font-bold text-background hover:text-primary transition-colors">
              Hair Art <span className="text-primary">Clinics</span>
            </LocalizedLink>
            <p className="text-background/60 mt-2 max-w-md text-sm">{t.aboutText}</p>
          </div>
          
          {/* Newsletter */}
          <div className="w-full lg:w-auto">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" />
              {t.newsletter}
            </h4>
            <p className="text-background/60 text-sm mb-3">{t.newsletterText}</p>
            <div className="flex gap-2">
              <Input 
                type="email" 
                placeholder={t.emailPlaceholder}
                className="bg-background/10 border-background/20 text-background placeholder:text-background/40 w-64"
              />
              <Button variant="default" size="default">
                {t.subscribe}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 py-12">
          {/* Locations */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              {t.locations}
            </h3>
            <div className="grid gap-4">
              {/* Den Haag */}
              <div className="bg-background/5 rounded-xl p-5 border border-background/10 hover:border-primary/30 transition-colors group">
                <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                  🇳🇱 Hair Art Clinics Rijswijk
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-background/70">Verrijn Stuartlaan 17</p>
                  <p className="text-background/70">2288 EK Rijswijk, Nederland</p>
                  <a href="tel:+31638523131" className="flex items-center gap-2 text-background/70 hover:text-primary transition-colors">
                    <Phone className="w-4 h-4" />
                    +31 6 38523131
                  </a>
                  <a
                    href="https://wa.me/31638523131"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-background/70 hover:text-primary transition-colors"
                    aria-label="WhatsApp +31 6 38523131"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp: +31 6 38523131
                  </a>
                  <a href="mailto:siostarr@hairartclinics.com" className="flex items-center gap-2 text-background/70 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4" />
                    siostarr@hairartclinics.com
                  </a>
                  <a
                    href="https://maps.google.com/?q=Verrijn+Stuartlaan+17+2288+EK+Rijswijk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline mt-2"
                  >
                    {t.getDirections} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

            </div>

            {/* Working Hours */}
            <div className="mt-4 flex items-center gap-2 text-sm text-background/60">
              <Clock className="w-4 h-4 text-primary" />
              <span>{t.weekdays}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.quickLinks}</h3>
            <ul className="space-y-3">
              {[
                { to: "/blog/haartransplantatie-den-haag-prijzen-en-diensten-in-the-hague", label: t.haartransplantatieDenHaag },
                { to: "/gallery", label: t.gallery },
                { to: "/blog", label: t.blog },
                { to: "/treatments", label: t.treatments },
                { to: "/book-consultation", label: t.bookConsultation },
                { to: "/hair-analysis", label: t.hairAnalysis },
              ].map((link) => (
                <li key={link.to}>
                  <LocalizedLink 
                    to={link.to} 
                    className="text-background/70 hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary/50 rounded-full group-hover:bg-primary transition-colors" />
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.legal}</h3>
            <ul className="space-y-3">
              {[
                { to: "/privacy-policy", label: t.privacy },
                { to: "/terms-of-service", label: t.terms },
                { to: "/gdpr-policy", label: t.kvkk },
                { to: "/cookie-policy", label: t.cookies },
              ].map((link) => (
                <li key={link.to}>
                  <LocalizedLink 
                    to={link.to} 
                    className="text-background/70 hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary/50 rounded-full group-hover:bg-primary transition-colors" />
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
              <li>
                <a
                  href="?cmpscreen"
                  onClick={(e) => {
                    // CMP yüklenmediyse dahili çerez tercih panelini aç
                    if (!openCookiePreferences()) {
                      e.preventDefault();
                    }
                  }}
                  className="cmpfooterlink cmpfooterlinkcmp text-background/70 hover:text-primary transition-colors flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary/50 rounded-full group-hover:bg-primary transition-colors" />
                  {PRIVACY_SETTINGS_LABEL[language as keyof typeof PRIVACY_SETTINGS_LABEL] || PRIVACY_SETTINGS_LABEL.en}
                </a>
              </li>

            </ul>

            {/* Social Media */}
            <div className="mt-8">
              <h4 className="font-semibold mb-4">{t.followUs}</h4>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full bg-background/10 flex items-center justify-center text-background/70 ${social.color} transition-all hover:scale-110 hover:bg-background/20`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Intermediary disclosure (Google/Meta ads policy) */}
        <div className="border-t border-background/10 py-6">
          <p className="text-background/70 text-sm leading-relaxed max-w-4xl">{t.intermediary}</p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-background/50 text-sm flex items-center gap-1">
            © {new Date().getFullYear()} Hair Art Clinics. {t.rights}
          </p>
          <p className="text-background/50 text-sm flex items-center gap-1">
            {t.madeWith} <Heart className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" /> in Den Haag, Nederland
          </p>
        </div>
      </div>
    </footer>
  );
};
